/**
 * @file add-to-cart.js
 *
 * Multi-item add-to-cart API endpoint logic.
 *
 * Page Template: src/templates/page.add-to-cart.liquid
 * Page Handle:   /pages/add-to-cart
 */

(function () {
  'use strict';

  console.log('========================================');
  console.log('🛒 ADD-TO-CART ENDPOINT INITIALISED');
  console.log('========================================');
  console.log('Timestamp:', new Date().toISOString());
  console.log('Current URL:', window.location.href);

  // ============================================================================
  // CONFIGURATION
  // ============================================================================

  var _config = {
    shopifyAjaxAddCartURL: '/cart/add.js',
    shopifyAjaxUpdateCartURL: '/cart/update.js',
    shopifyAjaxGetCartURL: '/cart.js'
  };

  // ============================================================================
  // PARSE URL PARAMETERS
  // ============================================================================

  var url_params = new URLSearchParams(window.location.search);

  console.log('📋 URL Parameters Detected:');
  url_params.forEach(function (value, key) {
    console.log('  • ' + key + ':', value);
  });

  // ============================================================================
  // MAIN
  // ============================================================================

  // 'redirect' only selects the destination; there is no non-redirect mode —
  // any missing/unrecognized value defaults to 'cart' (a product-page-relative
  // default doesn't work here since a single request can add several variants).
  var redirectTarget = url_params.get('redirect') === 'checkout' ? 'checkout' : 'cart';

  var athleteId = url_params.get('athlete_id');
  var sportId = url_params.get('sport_id');
  var shareWith = normalizeShareWith(url_params.get('share_with'));
  var overwriteProduct = url_params.get('overwrite_product') === 'true';

  showOverlay('Processing...');

  run();

  function run() {
    var items = parseItems(url_params.get('items'));
    if (!items) {
      return respondError('Invalid or missing "items" parameter. Expected a JSON-encoded array of line items.');
    }
    if (items.length === 0) {
      return respondError('"items" array is empty — nothing to add to cart.');
    }

    var invalid = items.filter(function (it) { return !it.id; });
    if (invalid.length > 0) {
      return respondError('One or more items are missing a variant_id.');
    }

    if (!athleteId || !sportId) {
      console.error('⚠️ Missing athlete_id or sport_id — recruiting bundle provisioning will fail downstream for this order (main cart flow continues).');
    }

    var customer = buildCustomerProperties(url_params);
    var participants = buildParticipants(url_params);

    // Stamp items with the theme's line-item property conventions:
    //   participant-linked items -> _participant_id — the key the
    //   cart grouping (cart.js getProductsPerParticipants), the participant
    //   cleanup flows and the React camp flow all use;
    //   participant-less items  -> _participant_id: 'guest' — the key the
    //   product-page gear flow (01-main.js) uses, so the cart's merchandise
    //   subtotal counts them.
    // Precedence: caller-provided properties.participant_id/_participant_id (set in
    // parseItems from entry.properties or entry.participant_id) always win here —
    // this block only fills in items that don't already have one, defaulting to the
    // first participant.
    if (participants.length > 0) {
      var defaultParticipantId = participants[0].id;
      items.forEach(function (item) {
        if (!item.properties) item.properties = {};
        if (!item.properties.participant_id && !item.properties._participant_id) {
          item.properties._participant_id = defaultParticipantId;
        }
      });

      // Catch a common integration mistake: an item's participant_id (explicit or
      // defaulted above) that doesn't match any participant in THIS request.
      var participantIds = participants.map(function (p) { return p.id; });
      items.forEach(function (item) {
        var pid = item.properties.participant_id || item.properties._participant_id;
        if (pid && participantIds.indexOf(pid) === -1) {
          console.warn('⚠️ Item participant_id "' + pid + '" does not match any participant in this request.', item);
        }
      });
    } else {
      items.forEach(function (item) {
        if (!item.properties) item.properties = {};
        if (!item.properties.participant_id && !item.properties._participant_id) {
          item.properties._participant_id = 'guest';
        }
      });
    }

    if (url_params.get('is_bundled') === 'true') {
      var bundleId = 'bundle_' + Date.now();
      items.forEach(function (item) {
        if (!item.properties) item.properties = {};
        if (!item.properties._bundle_id && !item.properties.bundle_id) {
          item.properties._bundle_id = bundleId;
        }
      });
      console.log('🔗 Bundled items under:', bundleId);
    }

    // Ecom's sharing grant (ShareWithApplier) reads ONLY line-item
    // properties._share_with — never the order-level attributes[share_with]
    // written below in updateCartAttributes. Without this, family sharing on
    // a coaching purchase silently does nothing: no error, buyer gets their
    // own sessions, nobody else does (MOB-1180). Every item in this request
    // gets it; ecom applies it only to lines whose entitlement allows
    // sharing.
    if (shareWith) {
      items.forEach(function (item) {
        if (!item.properties) item.properties = {};
        if (!item.properties._share_with) {
          item.properties._share_with = shareWith;
        }
      });
    }

    console.log('📦 Items to add:', items);
    console.log('👤 Customer:', customer);
    console.log('👥 Participants:', participants);

    updateOverlay('Adding to cart...');

    // MERGE, never replace: the cart may already carry participants/customer
    // from the camp selector (whose React flow reads the existing attribute and
    // appends — see order-review.js initCurrentCampers). Read the current cart
    // first, upsert our participants by id, and only touch the customer
    // attribute when this call actually carries customer data.
    getCart()
      .then(function (existingCart) {
        // overwrite_product=true: Back from /cart or /checkout to the linking
        // page and clicking the same button again must not re-add variants the
        // cart already holds. If nothing is left to add, the cart (items AND
        // attributes) is left untouched.
        var itemsToAdd = overwriteProduct ? filterItemsNotInCart(items, existingCart) : items;
        if (itemsToAdd.length === 0) {
          console.log('⏭️ overwrite_product: every item is already in the cart; skipping add');
          return null;
        }
        var merged = mergeCartAttributes(existingCart, customer, participants);
        console.log('🔀 Merged attributes:', merged);
        return addItems(itemsToAdd).then(function (addResponse) {
          console.log('✅ Items added:', addResponse);
          return updateCartAttributes(merged.customer, merged.participants, { athleteId: athleteId, sportId: sportId, shareWith: shareWith }).then(function () {
            return addResponse;
          });
        });
      })
      .then(function () {
        // #1118: /checkout loads no theme page, so the marketing attribution
        // must be on the cart before we leave. Never blocks the purchase.
        return window.imgMarketingParams ? window.imgMarketingParams.syncWithin(3000) : null;
      })
      .then(function () {
        // replace(), not href: drop this endpoint from session history so the
        // browser Back button from /cart or /checkout returns to the page that
        // linked here instead of re-running the add-to-cart (duplicate items).
        if (redirectTarget === 'checkout') {
          updateOverlay('Redirecting to checkout...');
          window.location.replace('/checkout');
          return;
        }
        updateOverlay('Redirecting to cart...');
        window.location.replace('/cart');
      })
      .catch(function (err) {
        console.error('❌ Add-to-cart flow failed:', err);
        respondError(
          err && err.message ? err.message : 'Add-to-cart request failed.',
          err && err.failed_items ? err.failed_items : null
        );
      });
  }

  /**
   * Merge this call's customer + participants into the cart's EXISTING
   * attributes (mirrors the React camp flow's append semantics):
   *   - participants: upsert by id — same-id entries are replaced (safe
   *     re-invocation), new ids are appended, existing campers are untouched.
   *   - customer: when this call carries at least one non-null customer field,
   *     it is shallow-merged ONTO the existing customer (same as
   *     router-other-locations.js / participantSelector.js); otherwise the
   *     existing customer attribute is kept as-is. Merging rather than
   *     replacing keeps keys this call doesn't set — notably a top-level
   *     `auth0_id` written earlier by 01-coaching-identity.js / router.js, which
   *     downstream buyer resolution reads from note_attributes.customer.auth0_id
   *     (one buyer per order; must stay a flat top-level key, never nested
   *     under participants) — plus rep attribution and referrer_data.
   */
  function mergeCartAttributes(cart, customer, participants) {
    var existingParticipants = [];
    var existingCustomer = null;
    var attrs = (cart && cart.attributes) || {};

    if (attrs.participants) {
      try {
        var parsed = JSON.parse(attrs.participants);
        if (Array.isArray(parsed)) existingParticipants = parsed;
      } catch (e) {
        console.warn('Existing participants attribute is not valid JSON; keeping ours only', e);
      }
    }
    if (attrs.customer) {
      try {
        existingCustomer = JSON.parse(attrs.customer);
      } catch (e) {
        console.warn('Existing customer attribute is not valid JSON; ignoring', e);
      }
    }

    var mergedParticipants = existingParticipants.slice();
    participants.forEach(function (p) {
      var idx = -1;
      for (var i = 0; i < mergedParticipants.length; i++) {
        if (mergedParticipants[i] && mergedParticipants[i].id === p.id) { idx = i; break; }
      }
      if (idx >= 0) mergedParticipants[idx] = p;
      else mergedParticipants.push(p);
    });

    var mergedCustomer = existingCustomer;
    if (customerHasData(customer)) {
      var base = (existingCustomer && typeof existingCustomer === 'object' && !Array.isArray(existingCustomer))
        ? existingCustomer
        : {};
      mergedCustomer = $.extend({}, base, customer);
    }

    return {
      participants: mergedParticipants,
      customer: mergedCustomer
    };
  }

  /**
   * overwrite_product=true: drop items whose variant is already in the cart.
   * Matched on variant id only — participant ids are generated per request,
   * so line-item properties can't tell a repeat click from a new one.
   */
  function filterItemsNotInCart(items, cart) {
    var cartVariantIds = ((cart && cart.items) || []).map(function (line) { return String(line.variant_id); });
    return items.filter(function (item) {
      var inCart = cartVariantIds.indexOf(String(item.id)) !== -1;
      if (inCart) console.log('⏭️ overwrite_product: variant ' + item.id + ' already in cart; not re-adding');
      return !inCart;
    });
  }

  function customerHasData(c) {
    if (!c) return false;
    return !!(c.email || c.first_name || c.last_name || c.contact_number
      || c.sport || c.author_id || c.auth0_id);
  }

  // ============================================================================
  // ITEM PARSING
  // ============================================================================

  /**
   * Parse the `items` query param into an array of objects shaped for
   * Shopify's /cart/add.js endpoint:
   *   { id: <variant_id>, quantity: <n>, selling_plan: <id> }
   *
   * Returns null if the param is missing or unparseable.
   */
  function parseItems(raw) {
    if (!raw) return null;

    var parsed;
    try {
      parsed = JSON.parse(raw);
    } catch (e) {
      console.error('Failed to JSON.parse items param:', e);
      return null;
    }

    if (!Array.isArray(parsed)) {
      console.error('items param did not decode to an array');
      return null;
    }

    return parsed.map(function (entry) {
      var item = {
        id: entry.variant_id || entry.id,
        quantity: parseInt(entry.quantity, 10) || 1
      };
      var sellingPlan = entry.selling_plan_id || entry.selling_plan;
      if (sellingPlan) {
        item.selling_plan = sellingPlan;
      }
      if (entry.properties && typeof entry.properties === 'object') {
        item.properties = entry.properties;
      }
      // Explicit per-item participant linkage: lets a multi-item + multi-participant
      // request say exactly which participant each line item belongs to, instead of
      // relying on the single-default-participant stamping in run(). Caller-supplied
      // properties.participant_id / _participant_id always win over this.
      if (entry.participant_id) {
        if (!item.properties) item.properties = {};
        if (!item.properties.participant_id && !item.properties._participant_id) {
          item.properties._participant_id = entry.participant_id;
        }
      }
      return item;
    });
  }

  // ============================================================================
  // CUSTOMER + PARTICIPANT BUILDERS
  // ============================================================================

  /**
   * Build the cart-level customer attribute object. Mirrors the structure used
   * by router.js / router-other-locations.js so the downstream Shopify flow
   * (checkout pre-fill, CRM transfer) keeps working identically.
   *
   * Adds `author_id` per the new endpoint brief.
   */
  function buildCustomerProperties(params) {
    var props = {
      email: params.get('email'),
      first_name: params.get('firstName'),
      last_name: params.get('lastName'),
      contact_number: params.get('phoneNumber'),
      sport: params.get('sport'),
      author_id: params.get('authorId') || params.get('author_id'),
      address: {
        country: params.get('parentCountry'),
        zipcode: params.get('parentZipcode')
      },
      marketing_data: {
        cRMChannel: params.get('cRMChannel') || 'organic',
        cRMName: params.get('cRMName') || 'imgacademy.com',
        cRMPointofEntry: params.get('cRMPointofEntry') || 'lead gate',
        cRMSource: params.get('cRMSource') || 'direct to site',
        cRMSpecificLeadGate: params.get('cRMSpecificLeadGate') || '18u &amp; 19+ camp builder lead form',
        cRMTacticName: params.get('cRMTacticName') || '_null_',
        cRMTactic: params.get('cRMTactic') || 'website',
        cRMOptedIn: params.get('cRMOptedIn'),
        cRMLeadGateLocation: params.get('cRMLeadGateLocation') || '18u &amp; 19+ camp builder lead form'
      },
      athlete: {
        email: params.get('email'),
        firstName: params.get('firstName'),
        lastName: params.get('lastName'),
        gender: params.get('gender'),
        phoneNumber: params.get('phoneNumber')
      },
      parent: {
        firstName: params.get('parentFirstName'),
        lastName: params.get('parentLastName'),
        email: params.get('parentEmail'),
        phoneNumber: params.get('parentPhoneNumber'),
        zipCode: params.get('parentZipcode'),
        country: params.get('parentCountry')
      }
    };

    // Buyer identity for downstream resolution: a single flat string at
    // customer.auth0_id. Only set when usable, so the merge in
    // mergeCartAttributes never clobbers an existing auth0_id with an empty
    // or "null"/"undefined" placeholder from a templated link.
    var auth0Id = normalizeAuth0Id(params.get('auth0_id'));
    if (auth0Id) {
      props.auth0_id = auth0Id;
    }

    return props;
  }

  function normalizeAuth0Id(raw) {
    if (raw == null) return '';
    var value = String(raw).trim();
    if (value === '' || value === 'null' || value === 'undefined') return '';
    return value;
  }

  /**
   * Normalize share_with to the JSON-array-string shape ecom's
   * ShareWithApplier requires (LineItem.ShareWith() /
   * apply_share_with.go) — anything else, e.g. a comma-joined string,
   * is a permanent provisioning failure (reason:malformed_share_with).
   * personal-coach.js already normalizes before building this endpoint's
   * URL, so this is mainly a safety net for a caller that hits
   * /pages/add-to-cart directly without going through that page first.
   * Mirrors personal-coach.js's normalizeShareWith exactly.
   *
   * @param {string} raw - raw share_with (URL param)
   * @returns {string} JSON-array string, or '' when there's nothing to send
   */
  function normalizeShareWith(raw) {
    var value = normalizeAuth0Id(raw);
    if (!value) return '';
    var trimmed = value.trim();
    if (trimmed.charAt(0) === '[') {
      try {
        var parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          var cleaned = parsed.filter(function (entry) {
            return normalizeAuth0Id(typeof entry === 'string' ? entry : String(entry)) !== '';
          });
          return cleaned.length ? JSON.stringify(cleaned) : '';
        }
      } catch (e) {
        // fall through — wrap the raw string as a single-entry array
      }
    }
    return JSON.stringify([trimmed]);
  }

  /**
   * Resolve the participants array. Two supported sources, in priority order:
   *   1. `participants` query param — JSON-encoded array of participant objects.
   *   2. Synthesised single participant from the flat athlete fields, matching
   *      router-other-locations.js behaviour (back-compat).
   */
  function buildParticipants(params) {
    var isNonCamp = params.get('non_camp') === 'true';

    var raw = params.get('participants');
    if (raw) {
      try {
        var parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          // Guarantee every participant an id (the docs promise it, the item
          // stamping and cart grouping depend on it). Index suffix keeps ids
          // unique within one call; React's max+1 allocator parses the numeric
          // prefix fine.
          var base = Date.now();
          parsed.forEach(function (p, idx) {
            if (!p || typeof p !== 'object') return;
            // Participants are passed through AS-IS — no field whitelist, so
            // future schema additions survive (a whitelist here nearly
            // destroyed citizenship_country/#1029 during live testing).
            // Downstream is safe with extras: the attribute rides Shopify as
            // an opaque JSON string and CRM reads only named fields.
            if (!p.id) p.id = 'participant_' + (base + idx);
            if (p.gender == null) p.gender = '';
            if (isNonCamp) p.non_camp = true;
          });
          return parsed.filter(function (p) { return p && typeof p === 'object'; });
        }
        console.warn('participants param did not decode to an array; ignoring');
      } catch (e) {
        console.warn('Failed to JSON.parse participants param; falling back to synthesised participant', e);
      }
    }

    if (!params.get('email') && !params.get('firstName')) {
      return [];
    }

    var participantId = 'participant_' + Date.now();
    return [buildSynthesisedParticipant(params, participantId, isNonCamp)];
  }

  function buildSynthesisedParticipant(params, participantId, isNonCamp) {
    var synthesised = {
      id: participantId,
      age: null,
      dob: null,
      email: params.get('email'),
      first_name: params.get('firstName'),
      last_name: params.get('lastName'),
      gender: params.get('gender') || '',
      phone: params.get('phoneNumber'),
      position: null,
      positionName: null,
      preferred_shirt_size: null,
      preferred_short_size: null,
      shirt_size: null,
      marketing_data: {
        cRMChannel: params.get('cRMChannel'),
        cRMName: params.get('cRMName'),
        cRMPointofEntry: params.get('cRMPointofEntry'),
        cRMSource: params.get('cRMSource'),
        cRMSpecificLeadGate: params.get('cRMSpecificLeadGate'),
        cRMTacticName: params.get('cRMTacticName'),
        cRMTactic: params.get('cRMTactic'),
        cRMOptedIn: params.get('cRMOptedIn'),
        cRMLeadGateLocation: params.get('cRMLeadGateLocation')
      }
    };
    if (isNonCamp) synthesised.non_camp = true;
    return synthesised;
  }

  // ============================================================================
  // SHOPIFY CART CALLS (promise wrappers around $.ajax)
  // ============================================================================

  function addItems(items) {
    return new Promise(function (resolve, reject) {
      $.ajax({
        type: 'POST',
        url: _config.shopifyAjaxAddCartURL,
        dataType: 'json',
        contentType: 'application/json',
        data: JSON.stringify({ items: items })
      })
        .done(function (response) { resolve(response); })
        .fail(function (xhr) {
          var msg = 'Failed to add items to cart';
          var failed = items.map(function (i) { return i.id; });
          try {
            var body = JSON.parse(xhr.responseText);
            if (body.description) msg = body.description;
            else if (body.message) msg = body.message;
          } catch (e) { /* keep default msg */ }
          var err = new Error(msg);
          err.failed_items = failed;
          reject(err);
        });
    });
  }

  function updateCartAttributes(customer, participants, recruiting) {
    var data = {};
    // Only write the attributes we actually have — a null customer (no data on
    // this call AND nothing pre-existing) or an empty participants list must
    // not overwrite / create empty attributes.
    if (customer) {
      data['attributes[customer]'] = JSON.stringify(customer);
    }
    if (participants && participants.length > 0) {
      data['attributes[participants]'] = JSON.stringify(participants);
    }
    if (recruiting) {
      if (recruiting.athleteId) data['attributes[athlete_id]'] = recruiting.athleteId;
      if (recruiting.sportId) data['attributes[sport_id]'] = recruiting.sportId;
      if (recruiting.shareWith) data['attributes[share_with]'] = recruiting.shareWith;
    }
    if (Object.keys(data).length === 0) {
      console.log('No cart attributes to update; skipping /cart/update.js call');
      return Promise.resolve({});
    }

    return new Promise(function (resolve, reject) {
      $.ajax({
        type: 'POST',
        url: _config.shopifyAjaxUpdateCartURL,
        dataType: 'json',
        data: data
      })
        .done(function (response) { resolve(response); })
        .fail(function (xhr) {
          reject(new Error('Failed to update cart attributes: ' + (xhr.responseText || xhr.status)));
        });
    });
  }

  function getCart() {
    return new Promise(function (resolve, reject) {
      $.ajax({
        type: 'GET',
        url: _config.shopifyAjaxGetCartURL,
        dataType: 'json'
      })
        .done(function (cart) { resolve(cart); })
        .fail(function (xhr) {
          reject(new Error('Failed to fetch cart: ' + (xhr.responseText || xhr.status)));
        });
    });
  }

  // ============================================================================
  // ERROR RENDERING
  // ============================================================================

  function respondError(message, failedItems) {
    console.error('❌', message, failedItems || '');
    showErrorOverlay(message);
  }

  // ============================================================================
  // OVERLAY (always shown — the endpoint always redirects, to /cart or /checkout)
  // Mirrors the UX of pages/router so external "Buy now" links get the same
  // polished loading experience.
  // ============================================================================

  function showOverlay(message) {
    injectOverlayStyles();
    var overlay = document.createElement('div');
    overlay.id = 'add-to-cart-overlay';
    overlay.className = 'atc-overlay';
    overlay.innerHTML =
      '<div class="atc-overlay-content">' +
        '<div class="atc-spinner"></div>' +
        '<p class="atc-message">' + escapeHtml(message) + '</p>' +
      '</div>';
    document.body.appendChild(overlay);
  }

  function updateOverlay(message) {
    var overlay = document.getElementById('add-to-cart-overlay');
    if (!overlay) return;
    var el = overlay.querySelector('.atc-message');
    if (el) el.textContent = message;
  }

  function showErrorOverlay(message) {
    injectOverlayStyles();
    var overlay = document.getElementById('add-to-cart-overlay');
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.id = 'add-to-cart-overlay';
      overlay.className = 'atc-overlay';
      document.body.appendChild(overlay);
    }
    overlay.innerHTML =
      '<div class="atc-overlay-content atc-error">' +
        '<div class="atc-error-icon">⚠️</div>' +
        '<h2>Something went wrong</h2>' +
        '<p>' + escapeHtml(message) + '</p>' +
      '</div>';
  }

  function escapeHtml(str) {
    if (str == null) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function injectOverlayStyles() {
    if (document.getElementById('add-to-cart-styles')) return;
    var styles = document.createElement('style');
    styles.id = 'add-to-cart-styles';
    styles.textContent =
      '.atc-overlay {' +
        'position: fixed; top: 0; left: 0; right: 0; bottom: 0;' +
        'background: rgba(0,0,0,0.8);' +
        'display: flex; align-items: center; justify-content: center;' +
        'z-index: 9999;' +
      '}' +
      '.atc-overlay-content {' +
        'background: #fff; padding: 40px; border-radius: 8px;' +
        'text-align: center; max-width: 400px;' +
        'box-shadow: 0 4px 20px rgba(0,0,0,0.3);' +
      '}' +
      '.atc-spinner {' +
        'width: 50px; height: 50px;' +
        'border: 4px solid #f3f3f3; border-top: 4px solid #3498db;' +
        'border-radius: 50%; animation: atc-spin 1s linear infinite;' +
        'margin: 0 auto 20px;' +
      '}' +
      '@keyframes atc-spin {' +
        '0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); }' +
      '}' +
      '.atc-message {' +
        'color: #333; font-size: 16px; margin: 0;' +
        'font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;' +
      '}' +
      '.atc-error { padding: 40px 30px; }' +
      '.atc-error-icon { font-size: 48px; margin-bottom: 20px; }' +
      '.atc-error h2 {' +
        'color: #e74c3c; margin: 0 0 10px; font-size: 24px;' +
        'font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;' +
      '}' +
      '.atc-error p {' +
        'color: #666; margin: 0;' +
        'font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;' +
      '}';
    document.head.appendChild(styles);
  }

})();
